import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState, type ReactNode } from 'react';

import { kindLabel, listCredentials, credentialKeys } from '../../entities/credential';
import { listDestinations } from '../../entities/destination';
import { flowKeys, listFlows } from '../../entities/flow';
import { listIssues } from '../../entities/issue';
import { EMPTY_RUN_FILTERS, listRuns, runFiltersToSearch, runKeys } from '../../entities/run';
import { listSources } from '../../entities/source';
import { KIND_LABEL, libraryKeys, listLibrary } from '../../entities/library';
import { searchSteps, usedInKeys } from '../../entities/used-in';
import { useDebouncedValue } from '../../shared/lib/use-debounced-value';
import { Kbd } from '../../shared/ui/kbd';
import { Shortcut } from '../../shared/ui/keycap';
import {
  BookmarksIcon,
  PlayIcon,
  CredentialIcon,
  DestinationsIcon,
  EventsIcon,
  FlowsIcon,
  IssuesIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  SourcesIcon,
  SpinnerIcon,
  TransformsIcon,
  type TIconProps,
} from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';

export type TNewFlowPath = 'one-step' | 'scratch';

// The ids the runs list answers `q` with (RunsController_list): a run, the request that
// started it, or one copy of it. A copy opens its own lane in the trace.
const RUN_ID = /^(run|req|evt)_[A-Za-z0-9]{6,}$/;
const LOOKUP_DAYS = 30;

interface ITarget {
  to: string;
  search?: Record<string, string | number>;
}

// Ids with a screen of their own; anything else that looks like an id says so instead of
// pretending to search for it.
const RECORD_OF: Record<string, { word: string; to: (id: string) => ITarget }> = {
  flw: { word: 'Open this flow', to: (id) => ({ to: `/flows/${id}` }) },
  src: { word: 'Open this source', to: (id) => ({ to: `/sources/${id}` }) },
  dst: { word: 'Open this destination', to: (id) => ({ to: `/destinations/${id}` }) },
  iss: { word: 'Open this issue', to: (id) => ({ to: `/issues/${id}` }) },
  trf: { word: 'Open in the Library', to: (id) => ({ to: `/library/${id}` }) },
  cust: {
    word: 'Show this customer’s events',
    to: (id) => ({
      to: '/events',
      search: { ...runFiltersToSearch({ ...EMPTY_RUN_FILTERS, customerId: [id] }) },
    }),
  },
};

const ANY_ID = /^([a-z]{3,4})_[A-Za-z0-9]{6,}$/;

const GO_TO: readonly { to: string; label: string; Icon: (props: TIconProps) => ReactNode }[] = [
  { to: '/flows', label: 'Flows', Icon: FlowsIcon },
  { to: '/events', label: 'Events', Icon: EventsIcon },
  { to: '/issues', label: 'Issues', Icon: IssuesIcon },
  { to: '/bookmarks', label: 'Bookmarks', Icon: BookmarksIcon },
  { to: '/sources', label: 'Sources', Icon: SourcesIcon },
  { to: '/destinations', label: 'Destinations', Icon: DestinationsIcon },
  { to: '/library', label: 'Library', Icon: TransformsIcon },
];

const SETTINGS: readonly { to: string; label: string }[] = [
  { to: '/settings/account', label: 'Account' },
  { to: '/settings/organization', label: 'Organization' },
  { to: '/settings/members', label: 'Members' },
  { to: '/settings/keys', label: 'API keys' },
  { to: '/settings/credentials', label: 'Credentials' },
  { to: '/settings/domains', label: 'Domains' },
  { to: '/settings/storage', label: 'Storage' },
  { to: '/settings/notifications', label: 'Notifications' },
];

// Every typed word, anywhere in the name: cmdk's fuzzy score let "cron" match "Stripe production".
const containsAll = (value: string, search: string, keywords?: string[]) => {
  const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase();
  return search
    .toLowerCase()
    .split(/\s+/)
    .every((word) => haystack.includes(word))
    ? 1
    : 0;
};

const Group = ({ heading, children }: { heading: string; children: ReactNode }) => (
  <Command.Group
    heading={heading}
    className="[&_[cmdk-group-heading]]:px-input [&_[cmdk-group-heading]]:pt-inset [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-group [&_[cmdk-group-heading]]:text-faint"
  >
    {children}
  </Command.Group>
);

const Item = ({
  value,
  keywords,
  onSelect,
  icon,
  children,
  meta,
  forceMount,
}: {
  value: string;
  keywords?: string[];
  onSelect: () => void;
  icon: ReactNode;
  children: ReactNode;
  meta?: ReactNode;
  forceMount?: boolean;
}) => (
  <Command.Item
    value={value}
    keywords={keywords}
    forceMount={forceMount}
    onSelect={onSelect}
    className="mx-label flex min-h-menu-item cursor-default items-center gap-inset rounded-control px-inset text-body text-primary data-[selected=true]:bg-hover-raised"
  >
    <span className="flex text-muted [&_svg]:size-icon-menu">{icon}</span>
    <span className="min-w-0 truncate">{children}</span>
    {meta && (
      <span className="ml-auto shrink-0 truncate pl-3 font-mono text-micro text-faint">{meta}</span>
    )}
  </Command.Item>
);

interface ICommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewFlow: (path: TNewFlowPath) => void;
}

export const CommandPalette = ({ open, onOpenChange, onNewFlow }: ICommandPaletteProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const typed = query.trim();
  const settled = useDebouncedValue(typed, 200);
  const searching = typed.length > 0;
  const runId = RUN_ID.test(settled) ? settled : null;
  const prefix = ANY_ID.exec(typed)?.[1];
  const record = prefix && !RUN_ID.test(typed) ? RECORD_OF[prefix] : undefined;
  const lookingUpId = Boolean(prefix);

  // Lists the org already has in the cache from its own screens; fetched on open, filtered here.
  const sources = useQuery({ queryKey: ['sources'], queryFn: listSources, enabled: open });
  const destinations = useQuery({
    queryKey: ['destinations'],
    queryFn: listDestinations,
    enabled: open,
  });
  const transforms = useQuery({
    queryKey: libraryKeys.list(),
    queryFn: () => listLibrary(),
    enabled: open,
  });
  const credentials = useQuery({
    queryKey: credentialKeys.list,
    queryFn: listCredentials,
    enabled: open,
  });
  // Flows can outgrow one page, so the server searches them.
  const flowQuery = { q: settled, limit: 8 };
  const flows = useQuery({
    queryKey: flowKeys.list(flowQuery),
    queryFn: () => listFlows(flowQuery),
    enabled: open && settled.length > 0 && !lookingUpId,
  });
  // Steps live inside flows, so the server reads the graphs; "helpdesk" finds "Deliver · Helpdesk".
  const steps = useQuery({
    queryKey: usedInKeys.steps(settled),
    queryFn: () => searchSteps(settled),
    enabled: open && settled.length > 1 && !lookingUpId,
  });
  const issues = useQuery({
    queryKey: ['issues', 'palette'],
    queryFn: () => listIssues({ view: 'active', limit: 50 }),
    enabled: open,
  });
  const runQuery = { q: runId ?? '', limit: 5 };
  const runs = useQuery({
    queryKey: runKeys.list(runQuery),
    // The list refuses a window over 31 days, and without `from` it searches only the last 24 h.
    queryFn: () =>
      listRuns({
        ...runQuery,
        from: new Date(Date.now() - LOOKUP_DAYS * 86_400_000).toISOString(),
      }),
    enabled: open && runId !== null,
  });

  const close = () => {
    onOpenChange(false);
    setQuery('');
  };
  const go = (to: string, search?: Record<string, string | number>) => {
    close();
    void navigate(search ? { to, search } : { to });
  };

  const pendingRun = RUN_ID.test(typed) && (settled !== typed || runs.isPending);

  // cmdk picks its first row when the search changes, not when rows arrive later: a run or
  // flow answered after the debounce would sit on top unselected and ↵ would open another.
  const [selected, setSelected] = useState('');
  const firstRun = runs.data?.data[0]?.id;
  const firstFlow = flows.data?.data[0]?.id;
  useEffect(() => {
    if (firstRun) setSelected(firstRun);
  }, [firstRun]);
  useEffect(() => {
    if (firstFlow) setSelected(firstFlow);
  }, [firstFlow]);
  const firstStep = steps.data?.[0];
  const firstStepValue = firstStep ? `step:${firstStep.flowId}:${firstStep.node.key}` : undefined;
  useEffect(() => {
    if (firstStepValue && !firstFlow) setSelected(firstStepValue);
  }, [firstStepValue, firstFlow]);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-scrim" />
        <Dialog.Content
          aria-label="Search"
          aria-describedby={undefined}
          className="fixed top-20 left-1/2 w-palette max-w-full -translate-x-1/2 overflow-hidden rounded-float border border-border-raised bg-surface shadow-panel"
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          <Command
            shouldFilter={!lookingUpId}
            filter={containsAll}
            value={selected}
            onValueChange={setSelected}
            loop
          >
            <Command.Input asChild value={query} onValueChange={setQuery}>
              <Input
                bare
                leading={<SearchIcon size={16} />}
                trailing={<Kbd>esc</Kbd>}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search flows, steps, sources, destinations, issues, code, or paste an id"
                className="h-palette-top border-b border-border-raised"
              />
            </Command.Input>

            <Command.List className="max-h-palette-list overflow-y-auto pb-label">
              {!RUN_ID.test(typed) && !record && !flows.isFetching && (
                <Command.Empty className="px-input py-4 text-data text-muted">
                  {lookingUpId
                    ? 'Nothing opens by that id here.'
                    : 'Nothing by that name. A run_, req_ or evt_ id opens its run; cust_ a customer’s events.'}
                </Command.Empty>
              )}

              {RUN_ID.test(typed) && (
                <Group heading="Runs">
                  {pendingRun && (
                    <p className="flex items-center gap-2 px-input py-2 text-data text-muted">
                      <SpinnerIcon /> Looking up {typed}
                    </p>
                  )}
                  {runs.isError && !pendingRun && (
                    <p className="px-input py-2 text-data text-danger-text">
                      The lookup failed. Try again in a moment.
                    </p>
                  )}
                  {!pendingRun &&
                    runs.data?.data.map((run) => (
                      <Item
                        key={run.id}
                        value={run.id}
                        forceMount
                        icon={<EventsIcon />}
                        meta={run.statusLabel}
                        onSelect={() =>
                          go(
                            `/events/runs/${run.id}`,
                            typed.startsWith('evt_') ? { copy: typed } : undefined,
                          )
                        }
                      >
                        {run.flowName ?? 'Deleted flow'} ·{' '}
                        <span className="font-mono">{run.id}</span>
                      </Item>
                    ))}
                  {!pendingRun && runs.isSuccess && runs.data.data.length === 0 && (
                    <p className="px-input py-2 text-data text-muted">
                      No flow run in the last {LOOKUP_DAYS} days carries{' '}
                      <span className="font-mono text-primary">{typed}</span>.
                    </p>
                  )}
                </Group>
              )}

              {record && (
                <Group heading="Exact match">
                  <Item
                    value={typed}
                    forceMount
                    icon={<SearchIcon />}
                    meta={record.word}
                    onSelect={() => {
                      const target = record.to(typed);
                      go(target.to, target.search);
                    }}
                  >
                    <span className="font-mono">{typed}</span>
                  </Item>
                </Group>
              )}

              {searching && !lookingUpId && (
                <>
                  {(flows.data?.data.length ?? 0) > 0 && (
                    <Group heading="Flows">
                      {flows.data?.data.map((flow) => (
                        <Item
                          key={flow.id}
                          value={flow.id}
                          keywords={[flow.name]}
                          icon={<FlowsIcon />}
                          meta={flow.state === 'active' ? flow.trigger.sourceName : flow.state}
                          onSelect={() => go(`/flows/${flow.id}`)}
                        >
                          {flow.name}
                        </Item>
                      ))}
                    </Group>
                  )}
                  {(steps.data?.length ?? 0) > 0 && (
                    <Group heading="Steps">
                      {steps.data?.map((hit) => (
                        <Item
                          key={`${hit.flowId}:${hit.node.key}`}
                          value={`step:${hit.flowId}:${hit.node.key}`}
                          keywords={[hit.node.name]}
                          icon={<FlowsIcon />}
                          meta={`${hit.flowName}${hit.onlyInDraft ? ' · draft' : ''}`}
                          onSelect={() => go(`/flows/${hit.flowId}`, { node: hit.node.key })}
                        >
                          {hit.node.name}
                        </Item>
                      ))}
                    </Group>
                  )}
                  <Group heading="Sources">
                    {sources.data?.data.map((source) => (
                      <Item
                        key={source.id}
                        value={source.id}
                        keywords={[source.name]}
                        icon={<SourcesIcon />}
                        onSelect={() => go(`/sources/${source.id}`)}
                      >
                        {source.name}
                      </Item>
                    ))}
                  </Group>
                  <Group heading="Destinations">
                    {destinations.data?.data.map((destination) => (
                      <Item
                        key={destination.id}
                        value={destination.id}
                        keywords={[destination.name]}
                        icon={<DestinationsIcon />}
                        onSelect={() => go(`/destinations/${destination.id}`)}
                      >
                        {destination.name}
                      </Item>
                    ))}
                  </Group>
                  <Group heading="Issues">
                    {issues.data?.data.map((issue) => (
                      <Item
                        key={issue.id}
                        value={issue.id}
                        keywords={[
                          issue.title,
                          issue.flowName ?? '',
                          issue.node?.name ?? '',
                          issue.sourceName ?? '',
                        ]}
                        icon={<IssuesIcon />}
                        meta={[issue.flowName, issue.node?.name].filter(Boolean).join(' · ')}
                        onSelect={() => go(`/issues/${issue.id}`)}
                      >
                        {issue.title}
                      </Item>
                    ))}
                  </Group>
                  <Group heading="Library">
                    {transforms.data?.data.map((transform) => (
                      <Item
                        key={transform.id}
                        value={transform.id}
                        keywords={[transform.name, KIND_LABEL[transform.kind]]}
                        icon={<TransformsIcon />}
                        meta={`${KIND_LABEL[transform.kind].toLowerCase()} · v${transform.latestVersion}`}
                        onSelect={() => go(`/library/${transform.id}`)}
                      >
                        <span className="font-mono">{transform.name}</span>
                      </Item>
                    ))}
                  </Group>
                  <Group heading="Credentials">
                    {credentials.data?.map((credential) => (
                      <Item
                        key={credential.id}
                        value={credential.id}
                        keywords={[credential.name]}
                        icon={<CredentialIcon />}
                        meta={kindLabel(credential, [])}
                        onSelect={() => go('/settings/credentials')}
                      >
                        {credential.name}
                      </Item>
                    ))}
                  </Group>
                </>
              )}

              {!lookingUpId && (
                <>
                  <Group heading="Actions">
                    {searching &&
                      flows.data?.data
                        .filter((flow) => flow.draft.edited)
                        .map((flow) => (
                          <Item
                            key={`test:${flow.id}`}
                            value={`action:test:${flow.id}`}
                            keywords={[flow.name, 'test draft']}
                            icon={<PlayIcon />}
                            meta="opens the test panel"
                            onSelect={() => go(`/flows/${flow.id}`, { test: '1' })}
                          >
                            Test draft of {flow.name}
                          </Item>
                        ))}
                    <Item
                      value="action:one-step"
                      keywords={['New flow', 'source to destination', 'create']}
                      icon={<PlusIcon />}
                      meta="source to destination"
                      onSelect={() => {
                        close();
                        onNewFlow('one-step');
                      }}
                    >
                      New flow
                    </Item>
                    <Item
                      value="action:scratch"
                      keywords={['New flow', 'from scratch', 'canvas', 'schedule', 'create']}
                      icon={<PlusIcon />}
                      meta="from scratch"
                      onSelect={() => {
                        close();
                        onNewFlow('scratch');
                      }}
                    >
                      New flow
                    </Item>
                  </Group>
                  <Group heading="Go to">
                    {GO_TO.map(({ to, label, Icon }) => (
                      <Item
                        key={to}
                        value={`go:${to}`}
                        keywords={[label]}
                        icon={<Icon />}
                        onSelect={() => go(to)}
                      >
                        {label}
                      </Item>
                    ))}
                  </Group>
                  <Group heading="Settings">
                    {SETTINGS.map(({ to, label }) => (
                      <Item
                        key={to}
                        value={`settings:${to}`}
                        keywords={[label]}
                        icon={<SettingsIcon />}
                        onSelect={() => go(to)}
                      >
                        {label}
                      </Item>
                    ))}
                  </Group>
                </>
              )}
            </Command.List>

            <div className="flex h-palette-foot items-center gap-4 border-t border-border-raised px-input text-micro text-faint">
              <span className="flex items-center gap-1">
                <Kbd>↑↓</Kbd> move
              </span>
              <span className="flex items-center gap-1">
                <Shortcut hint="↵" className="ml-0 pl-0" /> open
              </span>
              <span className="flex items-center gap-1">
                <Kbd>esc</Kbd> close
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
